import React, { useState } from 'react';
import { Plus, Edit2, Trash2, X, Eye, EyeOff } from 'lucide-react';
import ImageUploadV2 from '../common/ImageUploadV2';

const CategoryConfigStep = ({ formData, updateFormData }) => {
    const showRegIdFields = !!formData.generateRegistrationId;
    const eventPrefix = formData.registrationIdPrefix || '';
    const showVcardToggle = formData.vcardInvite?.enabled && formData.vcardInvite?.mode === 'per-category';
    const categories = formData.participantCategories || [];
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [amenityInput, setAmenityInput] = useState('');

    const isAddingNew = editingId && !categories.find(c => c.id === editingId);

    const startAdd = () => {
        const newId = `cat-${Date.now()}`;
        setEditForm({ id: newId, name: '', description: '', idCardBackground: '', amenities: [], showInForm: true, order: categories.length });
        setEditingId(newId);
        setAmenityInput('');
    };

    const startEdit = (cat) => {
        setEditForm({ ...cat, amenities: [...(cat.amenities || [])] });
        setEditingId(cat.id);
        setAmenityInput('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
        setAmenityInput('');
    };

    const saveEdit = () => {
        if (!editForm.name.trim()) return;
        const existing = categories.find(c => c.id === editForm.id);
        if (existing) {
            updateFormData({ participantCategories: categories.map(c => c.id === editForm.id ? editForm : c) });
        } else {
            updateFormData({ participantCategories: [...categories, editForm] });
        }
        cancelEdit();
    };

    const deleteCategory = (id) => {
        if (!window.confirm('Delete this category?')) return;
        updateFormData({ participantCategories: categories.filter(c => c.id !== id) });
    };

    const toggleVisibility = (id) => {
        updateFormData({
            participantCategories: categories.map(c =>
                c.id === id ? { ...c, showInForm: c.showInForm === false ? true : false } : c
            ),
        });
    };

    const addAmenity = () => {
        const val = amenityInput.trim();
        if (!val || editForm.amenities?.includes(val)) return;
        setEditForm(prev => ({ ...prev, amenities: [...(prev.amenities || []), val] }));
        setAmenityInput('');
    };

    const removeAmenity = (amenity) => {
        setEditForm(prev => ({ ...prev, amenities: prev.amenities.filter(a => a !== amenity) }));
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Participant Categories</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Create categories to give different ID card backgrounds and amenities to participant groups (e.g. Delegate, Speaker, Volunteer).
                    </p>
                </div>
                <button
                    type="button"
                    onClick={startAdd}
                    disabled={!!editingId}
                    className="flex-shrink-0 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Category
                </button>
            </div>

            {/* New category form */}
            {isAddingNew && (
                <CategoryForm
                    form={editForm}
                    setForm={setEditForm}
                    amenityInput={amenityInput}
                    setAmenityInput={setAmenityInput}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                    onAddAmenity={addAmenity}
                    onRemoveAmenity={removeAmenity}
                    isNew
                    showRegIdFields={showRegIdFields}
                    eventPrefix={eventPrefix}
                    showVcardToggle={showVcardToggle}
                />
            )}

            {/* Empty state */}
            {categories.length === 0 && !isAddingNew && (
                <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                    <div className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                        <Plus className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-gray-700 font-medium">No categories yet</p>
                    <p className="text-gray-400 text-sm mt-1">
                        Categories are optional. Skip this step if all participants share the same ID card.
                    </p>
                </div>
            )}

            {/* Category list */}
            {categories.length > 0 && (
                <div className="space-y-3">
                    {categories.map((cat) => (
                        <div key={cat.id} className="border border-gray-200 rounded-xl overflow-hidden">
                            {editingId === cat.id ? (
                                <CategoryForm
                                    form={editForm}
                                    setForm={setEditForm}
                                    amenityInput={amenityInput}
                                    setAmenityInput={setAmenityInput}
                                    onSave={saveEdit}
                                    onCancel={cancelEdit}
                                    onAddAmenity={addAmenity}
                                    onRemoveAmenity={removeAmenity}
                                    isNew={false}
                                    showRegIdFields={showRegIdFields}
                                    eventPrefix={eventPrefix}
                                    showVcardToggle={showVcardToggle}
                                />
                            ) : (
                                <div className="p-4 flex items-start gap-4 bg-white">
                                    {/* ID card thumbnail */}
                                    <div className="flex-shrink-0 w-14 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                                        {cat.idCardBackground ? (
                                            <img
                                                src={cat.idCardBackground}
                                                alt={cat.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <span className="text-gray-400 text-xs text-center leading-tight px-1">No BG</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-semibold text-gray-900">{cat.name}</p>
                                            {cat.showInForm === false ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full border border-gray-200">
                                                    <EyeOff className="w-3 h-3" /> Hidden in form
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded-full border border-green-100">
                                                    <Eye className="w-3 h-3" /> Visible in form
                                                </span>
                                            )}
                                        </div>
                                        {cat.description && (
                                            <p className="text-sm text-gray-500 mt-0.5">{cat.description}</p>
                                        )}
                                        {cat.amenities?.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {cat.amenities.map(a => (
                                                    <span
                                                        key={a}
                                                        className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full border border-emerald-100"
                                                    >
                                                        {a}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {!cat.amenities?.length && (
                                            <p className="text-xs text-gray-400 mt-1">No amenities</p>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => toggleVisibility(cat.id)}
                                            disabled={!!editingId}
                                            title={cat.showInForm === false ? 'Show in public form' : 'Hide from public form'}
                                            className={`p-2 rounded-lg disabled:opacity-40 transition-colors ${cat.showInForm === false ? 'text-gray-400 hover:bg-gray-100' : 'text-green-600 hover:bg-green-50'}`}
                                        >
                                            {cat.showInForm === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => startEdit(cat)}
                                            disabled={!!editingId}
                                            title="Edit"
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-40"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteCategory(cat.id)}
                                            disabled={!!editingId}
                                            title="Delete"
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-40"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                    Only categories marked <strong>Visible in form</strong> appear on the public registration page. Hidden categories can still be assigned by admins via Quick Add.
                </p>
            </div>
        </div>
    );
};

const CategoryForm = ({ form, setForm, amenityInput, setAmenityInput, onSave, onCancel, onAddAmenity, onRemoveAmenity, isNew, eventPrefix, showRegIdFields, showVcardToggle }) => {
    return (
        <div className="p-5 bg-blue-50 border border-blue-200 rounded-xl space-y-5">
            <h3 className="font-semibold text-gray-900">{isNew ? 'New Category' : 'Edit Category'}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.name || ''}
                        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Delegate, Speaker, Volunteer"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                    <input
                        type="text"
                        value={form.description || ''}
                        onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Short description"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                </div>
            </div>

            <ImageUploadV2
                onUploadSuccess={url => setForm(prev => ({ ...prev, idCardBackground: url || '' }))}
                onUploadError={err => console.error('Upload error:', err)}
                uploadEndpoint="/api/v2/events/upload/event-background"
                currentImageUrl={form.idCardBackground || null}
                label="ID Card Background for this Category"
            />

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
                <div className="flex gap-2 mb-2">
                    <input
                        type="text"
                        value={amenityInput}
                        onChange={e => setAmenityInput(e.target.value)}
                        onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); onAddAmenity(); } }}
                        placeholder="e.g., Lunch, Wifi, Parking, T-Shirt"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                    <button
                        type="button"
                        onClick={onAddAmenity}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                    >
                        Add
                    </button>
                </div>
                {form.amenities?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {form.amenities.map(a => (
                            <span
                                key={a}
                                className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 text-sm rounded-full border border-emerald-100"
                            >
                                {a}
                                <button
                                    type="button"
                                    onClick={() => onRemoveAmenity(a)}
                                    className="ml-2 text-emerald-400 hover:text-emerald-600"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 italic">No amenities added yet.</p>
                )}
            </div>

            {/* Per-category registration ID config */}
            {showRegIdFields && (
                <div className="border border-dashed border-blue-300 bg-blue-50/40 rounded-xl p-4 space-y-3">
                    <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">Registration ID — Category Override</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Sub-Prefix <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <div className="flex items-center gap-2">
                                {eventPrefix && (
                                    <span className="text-xs font-mono bg-gray-100 border border-gray-300 px-2 py-2 rounded-lg text-gray-600 shrink-0">
                                        {eventPrefix}-
                                    </span>
                                )}
                                <input
                                    type="text"
                                    value={form.registrationIdSubPrefix || ''}
                                    onChange={e => setForm(prev => ({ ...prev, registrationIdSubPrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                                    placeholder="e.g., BY, HB, EX"
                                    maxLength={10}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm uppercase"
                                />
                            </div>
                            {form.registrationIdSubPrefix && (
                                <p className="text-xs text-blue-600 mt-1">
                                    Full prefix: <code className="font-mono bg-blue-100 px-1 rounded">{eventPrefix ? `${eventPrefix}-${form.registrationIdSubPrefix}` : form.registrationIdSubPrefix}</code>
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Start Number <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                                type="number"
                                min={1}
                                value={form.registrationIdStartNumber || ''}
                                onChange={e => {
                                    const val = parseInt(e.target.value, 10);
                                    setForm(prev => ({ ...prev, registrationIdStartNumber: (!isNaN(val) && val >= 1) ? val : null }));
                                }}
                                placeholder="e.g., 251"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                            <p className="text-xs text-gray-400 mt-1">Leave blank to use the event default</p>
                        </div>
                    </div>
                </div>
            )}

            {/* vCard invitation toggle — only when event has per-category mode */}
            {showVcardToggle && (
                <div className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div>
                        <p className="text-sm font-medium text-gray-700">Send vCard Invitation</p>
                        <p className="text-xs text-gray-400 mt-0.5">Participants in this category will receive a vCard signup invite</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, vcardInviteEnabled: !prev.vcardInviteEnabled }))}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${form.vcardInviteEnabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.vcardInviteEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                </div>
            )}

            {/* Show in public form toggle */}
            <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                <div>
                    <p className="text-sm font-medium text-gray-700">Show in public registration form</p>
                    <p className="text-xs text-gray-400 mt-0.5">Participants will see and select this category when registering</p>
                </div>
                <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, showInForm: prev.showInForm === false ? true : false }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${form.showInForm === false ? 'bg-gray-300' : 'bg-blue-600'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${form.showInForm === false ? 'translate-x-1' : 'translate-x-6'}`} />
                </button>
            </div>

            <div className="flex gap-3 pt-1">
                <button
                    type="button"
                    onClick={onSave}
                    disabled={!form.name?.trim()}
                    className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {isNew ? 'Add Category' : 'Save Changes'}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-5 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-white"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default CategoryConfigStep;
